import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { PLANS, type Billing, type Plan } from "../../../shared/types.ts";
import { PlanCards } from "../../landing/Closing.tsx";
import { fade, h2 } from "../../landing/styles.ts";
import { Button, cn, ease } from "../../ui/index.ts";
import { errorText, getBilling, openPortal, startCheckout } from "../../lib/api.ts";
import { useApp } from "../../lib/ctx.ts";
import { day, num } from "../../lib/format.ts";
import type { Paid } from "../../lib/plan.ts";

const ORDER: Plan[] = ["free", "autopilot", "max"];
/** The paid plans above `p`, cheapest first. */
export const above = (p: Plan) => ORDER.slice(ORDER.indexOf(p) + 1) as Paid[];

/**
 * Starts Checkout for a plan, or opens the billing portal, and leaves the page for Stripe. `busy` is what is on its way
 * there; it clears when the browser comes back to this page from its back-forward cache.
 */
export function useCheckout() {
  const app = useApp();
  const [busy, setBusy] = useState<Paid | "portal" | null>(null);

  useEffect(() => {
    const back = (e: PageTransitionEvent) => e.persisted && setBusy(null);
    addEventListener("pageshow", back);
    return () => removeEventListener("pageshow", back);
  }, []);

  const go = (action: Paid | "portal") => {
    setBusy(action);
    (action === "portal" ? openPortal() : startCheckout(action)).then(
      ({ url }) => location.assign(url),
      (e) => {
        setBusy(null);
        app.toast(errorText(e), "bad");
      },
    );
  };

  return { busy, go };
}

/** `/upgrade`: this month's usage, then the landing's three plan cards with a checkout on each plan above the user's. */
export function Upgrade() {
  const [billing, setBilling] = useState<Billing | null>(null);
  const [failed, setFailed] = useState(false);
  const { busy, go } = useCheckout();

  useEffect(() => {
    getBilling().then(setBilling, () => setFailed(true));
  }, []);

  const plan = billing?.plan ?? "free";
  const upgrades = above(plan);

  return (
    <div className="flex flex-col gap-10 pb-6 md:gap-14 md:pt-4">
      <header className="flex flex-col items-center gap-6 text-center">
        <h1 className={cn(h2, "max-w-[16ch]")}>
          Apply to every job <span className={fade}>that fits.</span>
        </h1>
        {billing && <Usage billing={billing} />}
      </header>
      <PlanCards
        inView={false}
        leadOnPhone
        className="w-full"
        action={(p, featured) => {
          const current = p === plan && !failed;
          const up = upgrades.includes(p as Paid);
          return (
            <Button
              variant={featured && !current ? "accent" : "secondary"}
              size={featured ? "lg" : "md"}
              className="relative mt-8 w-full"
              loading={!current && busy === (up ? p : "portal")}
              disabled={current || busy !== null || (!billing && !failed)}
              onClick={() => go(up ? (p as Paid) : "portal")}
            >
              {current ? "Your plan" : up ? `Upgrade to ${PLANS[p].name}` : "Manage billing"}
            </Button>
          );
        }}
      />
    </div>
  );
}

/** This month's applications against the plan's allowance, so the reason to upgrade is a number. */
function Usage({ billing }: { billing: Billing }) {
  const full = billing.used >= billing.limit;
  return (
    <div className="w-full max-w-sm rounded-card bg-surface px-5 py-4 text-left shadow-card">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm text-ink-soft tabular-nums">
          <span className="font-bold text-ink">{num(Math.min(billing.used, billing.limit))}</span> of {num(billing.limit)} used this month
        </p>
        <p className="shrink-0 text-[13px] text-muted">
          {billing.plan === "free" ? "Resets" : billing.cancelAtPeriodEnd ? "Ends" : "Renews"} {day(billing.periodEnd)}
        </p>
      </div>
      <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-sunk" role="presentation">
        <motion.div
          className={cn("h-full rounded-full", full ? "bg-bad" : "bg-primary")}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, (billing.used / Math.max(1, billing.limit)) * 100)}%` }}
          transition={{ duration: 0.5, ease }}
        />
      </div>
    </div>
  );
}
